import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { useRouter } from "@tanstack/react-router"
import { runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { useEffect, useRef, useState } from "react"
import { combineLatest, from, race, timer } from "rxjs"
import { switchMap, tap } from "rxjs/operators"

import { Button } from "@/components/Button/Button.tsx"
import { CopyableCodeSnippet } from "@/components/CopyableCodeSnippet/CopyableCodeSnippet.tsx"
import { ConfirmDialog, Dialog } from "@/components/Dialog/Dialog.tsx"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { Heading3 } from "@/components/Heading/Heading.tsx"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"
import { TerminalDialog } from "@/components/ReasoningStep/TerminalDialog.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Squircle } from "@/components/Squircle/Squircle.tsx"
import { StopButton, StopIcon } from "@/components/StopButton/StopButton.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"
import {
  CheckmarkRegular,
  ClockRegular,
  CreateRegular,
  DeleteLight,
  Icon16,
  InfoRegular,
  PlayFill,
  SpinnerRegular,
  StopRegular,
  TerminalRegular
} from "@/icons/Icons.ts"
import { formatDuration } from "@/lib/date.ts"
import { AddSecretDialog } from "@/routes/projects/secrets/AddSecretDialog.tsx"
import { ProjectSecretsViewModel } from "@/routes/projects/secrets/ProjectSecretsViewModel.ts"
import { AddPreviewScriptDialog } from "@/routes/tasks/TaskViewScene/InitRuntimeConfig/AddPreviewScriptDialog.tsx"
import { ProjectSecretsDialog } from "@/routes/tasks/TaskViewScene/InitRuntimeConfig/ProjectSecretsDialog.tsx"
import { ScriptEditDialogContent } from "@/routes/tasks/TaskViewScene/InitRuntimeConfig/ScriptEditDialogContent.tsx"
import { TaskRuntimeConfigFileModel } from "@/routes/tasks/TaskViewScene/InitRuntimeConfig/TaskRuntimeConfigFileModel.ts"
import { DownloadRuntimeLogsButton } from "@/routes/tasks/TaskViewScene/PreviewWidgetContent.tsx"
import { SshKeysOnboarding } from "@/routes/tasks/TaskViewScene/SshKeysOnboarding.tsx"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type {
  RuntimeConfig,
  TID,
  UPreviewProcessesApiState,
  UProcessRunState
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskPreviewModel } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { LinariaClassName } from "@linaria/core"
import type { IObservableValue } from "mobx"
import type { ReactNode } from "react"

type StatusProps = {
  text: string
  icon: ReactNode
  cardClass: string
  statusClass: string
}

const notStartedStatusProps = (): StatusProps => ({
  text: "Not started",
  icon: <ClockRegular className={Icon16} />,
  cardClass: styles.CardDisabled,
  statusClass: styles.StatusDisabled
})

const runningStatusProps = (): StatusProps => ({
  text: "Running",
  icon: <SpinnerRegular className={Icon16} />,
  cardClass: styles.CardInfo,
  statusClass: styles.StatusInfo
})

const previewCommandRunningStatusProps = (): StatusProps => ({
  text: "Running",
  icon: <ClockRegular className={Icon16} />,
  cardClass: styles.CardSuccess,
  statusClass: styles.StatusSuccess
})

const previewCommandLaunchingStatusProps = (): StatusProps => ({
  text: "Launching",
  icon: <SpinnerRegular className={Icon16} />,
  cardClass: styles.CardInfo,
  statusClass: styles.StatusInfo
})

const successStatusProps = (): StatusProps => ({
  text: "Success",
  icon: <CheckmarkRegular className={Icon16} />,
  cardClass: styles.CardSuccess,
  statusClass: styles.StatusSuccess
})

const failedStatusProps = (): StatusProps => ({
  text: "Failed",
  icon: <StopRegular className={Icon16} />,
  cardClass: styles.CardError,
  statusClass: styles.StatusError
})

const getWarmupScriptStatusProps = (state?: UProcessRunState): StatusProps => {
  switch (state?.className) {
    case "Running":
      return runningStatusProps()
    case "Finished":
      return (state.exitCode === 0) ? successStatusProps() : failedStatusProps()
    default:
      return notStartedStatusProps()
  }
}

const usePreviewScriptStatusProps = (state?: UProcessRunState): StatusProps => {
  switch (state?.className) {
    case "Running":
      return state.ready ? previewCommandRunningStatusProps() : previewCommandLaunchingStatusProps()
    case "Finished":
      return failedStatusProps()
    default:
      return notStartedStatusProps()
  }
}

export const TaskRuntimeConfigFileTab = observer((props: {
  model: TaskViewModel
}) => {
  const context = useOrgContext()
  const router = useRouter()
  const task = props.model.task.get()

  const model = useModel(() => {
    return new TaskRuntimeConfigFileModel({
      context,
      router,
      queries: context.queries,
      taskViewModel: props.model,
      task
    })
  }, [props.model, task, context])

  const previewModel = props.model.currentPreview.get() || null

  const isReadonly = props.model.readonly.get()

  // Add script dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  return (
    <Container>
      <HeaderContainer>
        <HeaderLine>
          This setup defines how Matter prepares and runs your app,<br />
          so you can preview it live and share it with your team.
        </HeaderLine>
      </HeaderContainer>

      {(previewModel && previewModel.listeningToPreviewProcessState.get()) ? (
          <>
            <PreviewEnvStatusCard model={model} previewModel={previewModel} isReadonly={isReadonly} />

            <WarmupScriptCard
              model={model}
              previewProcessesState={previewModel.previewProcessesState}
              isReadonly={isReadonly}
            />

            {model.previewCommandsState.get().map(({ command, state }, index) => (
              <PreviewScriptCard
                key={command.id}
                commandRunState={state}
                command={command}
                index={index}
                model={model}
                isReadonly={isReadonly}
              />
            ))}

            <FullWidthColumn>
              {!isReadonly && (
                <Horizontal gap="8px">
                  <ButtonWithIcon kind="alternative" onClick={() => setAddDialogOpen(true)}>
                    <CreateRegular className={Icon16} />
                    Add script
                  </ButtonWithIcon>
                  <ConfigureSecretsButton model={props.model} />
                </Horizontal>
              )}
              <AddPreviewScriptDialog
                open={addDialogOpen}
                onOpenChange={(v) => setAddDialogOpen(v)}
                isReadonly={isReadonly}
                onAdd={async (id, title, script) => {
                  await model.handleAddPreviewCommand(id, title, script)
                }}
              />
            </FullWidthColumn>
          </>
        )
        : <Spinner text={ellipsize("Loading")} />
      }
    </Container>
  )
})

const ConfigureSecretsButton = observer(({ model }: { model: TaskViewModel }) => {
  const context = useOrgContext()
  const project = model.project

  const [secretsDialogOpen, setSecretsDialogOpen] = useState(false)
  const [addSecretDialogOpen, setAddSecretDialogOpen] = useState(false)

  const secretsModel = useModel(() => new ProjectSecretsViewModel(context, project.get().key, model.taskId))
  const secretsCount = secretsModel.secrets.get().length

  const isReadonly = model.readonly.get()

  const handleButtonClick = () => {
    if (secretsCount === 0) {
      setAddSecretDialogOpen(true)
    } else {
      setSecretsDialogOpen(true)
    }
  }

  const handleAddSecret = async (key: string, description: string, value: string) => {
    await secretsModel.addSecret(key, description, value)
    setSecretsDialogOpen(true)
  }

  return (
    <>
      <Button kind="alternative" onClick={handleButtonClick} disabled={isReadonly}>
        Configure secrets {secretsCount > 0 ? `(${secretsCount})` : ""}
      </Button>

      <ProjectSecretsDialog
        open={secretsDialogOpen}
        onOpenChange={setSecretsDialogOpen}
        readOnly={isReadonly}
        model={secretsModel}
      />

      <AddSecretDialog
        isOpen={addSecretDialogOpen}
        onOpenChange={setAddSecretDialogOpen}
        onAddSecret={handleAddSecret}
      />
    </>
  )
})

const WarmupScriptCard = observer(({ model, isReadonly }: {
  model: TaskRuntimeConfigFileModel
  previewProcessesState: IObservableValue<UPreviewProcessesApiState | null>
  isReadonly: boolean
}) => {
  const previewProcessesState = model.previewProcessesState.get()

  const warmupScriptState = (previewProcessesState?.className == "Running") ? previewProcessesState.warmupScriptState : undefined
  const statusProps = getWarmupScriptStatusProps(warmupScriptState)
  const isRunning = warmupScriptState?.className == "Running"
  const isSuccess = warmupScriptState?.className == "Finished" && warmupScriptState?.exitCode == 0
  const logIdByScriptId = model.runtimeState.get()?.logIdByScriptId ?? {}
  const beforeLogId: string | undefined = logIdByScriptId["before"]

  const [startPressed, setStartPressed] = useState(false)
  const [stopPressed, setStopPressed] = useState(false)

  useEffect(() => {
    setStartPressed(false)
    setStopPressed(false)
  }, [previewProcessesState, warmupScriptState])

  return (
    <Squircle className={cx(Card, statusProps.cardClass) as LinariaClassName}>
      <FullWidthRow alignItems="flex-start">
        <FullWidthColumn>
          <FullWidthRow alignItems="center">
            <CardTitle>Warmup Script</CardTitle>
            <Spring />
            <Status props={statusProps} />
          </FullWidthRow>
          <CardDescription>
            <MarkdownWrapper
              text="The warmup script installs dependencies and build assets for both frontend and backend"
            />
          </CardDescription>
          <FullWidthRow alignItems="center">
            <Dialog
              title="Warmup Script"
              trigger={(
                <CardButton kind="alternative" size="large">
                  <TerminalRegular className={Icon16} />
                  Edit script
                </CardButton>
              )}
              maxWidth="100%"
            >
              <ScriptEditDialogContent
                initialValue={model.beforeCommand.get()}
                onSave={(value) => model.handleBeforeCommandChange(value)}
                onSaveAndRun={(value) => model.handleBeforeCommandChange(value).then(() => model.handleRunBeforeCommand())}
                isReadonly={isReadonly}
                canRun={!isRunning && !model.isAnyScriptRunning.get()}
              />
            </Dialog>
            {beforeLogId && model.task && (
              <ViewLogsButton
                taskId={model.task.id}
                execResultId={beforeLogId}
                title="Warmup Script"
                commandId={beforeLogId}
                previewId={model.taskViewModel.previewId.get() || undefined}
              />
            )}
            <Spring />
            <InlineAction className={cx(isSuccess ? styles.HoverOnly : undefined)}>
              {isRunning ? (
                <Tooltip content="Stop">
                  <StopButton
                    kind="icon"
                    onClick={() => {
                      setStopPressed(true)
                      void model.handleStopBeforeCommand()
                    }}
                    disabled={stopPressed}
                    aria-label="Stop"
                  >
                    <StopIcon />
                  </StopButton>
                </Tooltip>
              ) : (
                !model.isAnyScriptRunning.get() && model.isEnvActive.get() && (
                  <Tooltip content="Run warmup script" align="end">
                    <RunButton
                      kind="icon"
                      onClick={() => {
                        if (!warmupScriptState || !isSuccess) {
                          setStartPressed(true)
                        }
                        void model.handleRunBeforeCommand()
                      }}
                      disabled={isReadonly || startPressed}
                      aria-label="Run warmup script"
                    >
                      <PlayFill className={Icon16} color="#FFF" />
                    </RunButton>
                  </Tooltip>
                )
              )}
            </InlineAction>
          </FullWidthRow>
        </FullWidthColumn>
      </FullWidthRow>
    </Squircle>
  )
})

const ViewLogsButton = (props: {
  taskId: string
  execResultId: string
  title: string
  commandId: string
  previewId?: string
  exitCode?: number | null
}) => {
  return (
    <TerminalDialog
      taskId={props.taskId}
      execResultId={props.execResultId as TID}
      title={props.title}
      commandId={props.commandId}
      previewId={props.previewId}
    >
      <CardButton kind="alternative">View logs</CardButton>
    </TerminalDialog>
  )
}

const PreviewScriptCard = observer(({ command, commandRunState: scriptRunState, index, model, isReadonly }: {
  command: RuntimeConfig.RunAppCommand
  commandRunState: UProcessRunState
  index: number
  model: TaskRuntimeConfigFileModel
  isReadonly: boolean
}) => {
  const statusProps = usePreviewScriptStatusProps(scriptRunState)
  const isRunning = scriptRunState.className == "Running"
  const isSuccess = scriptRunState.className == "Running" && scriptRunState.ready
  const hoverOnlyClass = isSuccess ? styles.HoverOnly : undefined
  const anyRunning = model.isAnyScriptRunning.get()
  const logIdByScriptId = model.runtimeState.get()?.logIdByScriptId ?? {}

  const [startPressed, setStartPressed] = useState(false)
  const [stopPressed, setStopPressed] = useState(false)

  useEffect(() => {
    setStartPressed(false)
    setStopPressed(false)
  }, [scriptRunState])

  const [confirmRemoveScriptDialogOpen, setConfirmRemoveScriptDialogOpen] = useState(false)

  return (
    <Squircle key={`preview-cmd-${index}`} className={cx(Card, statusProps.cardClass) as LinariaClassName}>
      <FullWidthRow alignItems="flex-start">
        <FullWidthColumn>
          <FullWidthRow alignItems="center">
            <CardTitle>{command.title}</CardTitle>
            {!isReadonly && (
              <InlineAction className={styles.HoverOnly}>
                <ConfirmDialog
                  open={confirmRemoveScriptDialogOpen}
                  onOpenChange={setConfirmRemoveScriptDialogOpen}
                  trigger={(
                    <Tooltip content={`Remove "${command.title}" script`}>
                      <Button
                        kind="icon"
                        aria-label={`Remove "${command.title}" script`}
                        onClick={(e) => {
                          e.stopPropagation()
                          setConfirmRemoveScriptDialogOpen(true)
                        }}
                      >
                        <DeleteLight />
                      </Button>
                    </Tooltip>
                  )}
                  description="Are you sure you want to remove this script?"
                  confirmText="Remove"
                  onConfirm={async () => {
                    await model.handleRemovePreviewCommand(command.id)
                  }}
                />
              </InlineAction>
            )}
            <Spring />
            <Status props={statusProps} />
          </FullWidthRow>
          {command.description && (
            <CardDescription>
              <MarkdownWrapper text={command.description} />
            </CardDescription>
          )}
          <FullWidthRow alignItems="center">
            <Dialog
              title={command.title}
              trigger={(
                <CardButton kind="alternative">
                  <TerminalRegular className={Icon16} />
                  Edit script
                </CardButton>
              )}
              maxWidth="100%"
            >
              <ScriptEditDialogContent
                initialValue={command.command}
                onSave={(value) => model.handlePreviewRunCommandChange(index, value)}
                onSaveAndRun={(value) => model.handlePreviewRunCommandChange(index, value).then(() => model.handleRunPreviewCommand(index))}
                isReadonly={isReadonly}
                canRun={!isRunning && !anyRunning}
              />
            </Dialog>
            {model.task && (
              <ViewLogsButton
                taskId={model.task.id}
                execResultId={logIdByScriptId[command.id]}
                title={command.title}
                commandId={command.id}
                previewId={model.taskViewModel.previewId.get() || undefined}
              />
            )}
            {scriptRunState.className === "Running" && scriptRunState.listenedPorts.length > 0 && (
              <span>listening to&nbsp;
                {scriptRunState.listenedPorts.map((port, index) => {
                  const isNotLast = index + 1 < scriptRunState.listenedPorts.length
                  return port.toString() + (isNotLast ? ", " : "")
                })}
              </span>
            )}
            <Spring />
            <InlineAction className={cx(hoverOnlyClass)}>
              {isRunning ? (
                <Tooltip content="Stop">
                  <StopButton
                    kind="icon"
                    onClick={() => {
                      setStopPressed(true)
                      void model.handleStopPreviewCommand(command.id)
                    }}
                    disabled={stopPressed}
                    aria-label="Stop"
                  >
                    <StopIcon />
                  </StopButton>
                </Tooltip>
              ) : (
                !anyRunning && model.isEnvActive.get() && (
                  <Tooltip content={`Run "${command.title}" script`} align="end">
                    <RunButton
                      kind="icon"
                      onClick={() => {
                        setStartPressed(true)
                        void model.handleRunPreviewCommand(index)
                      }}
                      disabled={isReadonly || startPressed}
                      aria-label={`Run "${command.title}" script`}
                    >
                      <PlayFill className={Icon16} color="#FFF" />
                    </RunButton>
                  </Tooltip>
                )
              )}
            </InlineAction>
          </FullWidthRow>
        </FullWidthColumn>
      </FullWidthRow>
    </Squircle>
  )
})

const getStartingProgress = (state: UPreviewProcessesApiState | null) => {
  if (state?.className !== "Starting") {
    return null
  }

  if (!state.expectedStartupDurationSeconds || !state.startedAt) {
    return null
  }

  const secondsPassed = Math.max(0, (new Date().getTime() - state.startedAt) / 1000)
  let secondsRemaining = Math.max(0, state.expectedStartupDurationSeconds - secondsPassed)
  if (secondsRemaining >= 60) {
    secondsRemaining = Math.round(secondsRemaining / 60) * 60
  }
  return {
    progress: Math.max(0, Math.min(1, secondsPassed / state.expectedStartupDurationSeconds)),
    remainingTimeHint: secondsRemaining <= 1 ? "Almost done" : `${formatDuration(secondsRemaining * 1000)} left`
  }
}

const getPreviewStatusProps = (state: UPreviewProcessesApiState | null, devMode: boolean): StatusProps => {
  switch (state?.className) {
    case "Starting": {
      const remainingTimeHint = devMode ? getStartingProgress(state)?.remainingTimeHint : undefined
      return {
        text: remainingTimeHint ? "Starting" + textStyles.Characters.Nbsp + textStyles.Characters.Nbsp + remainingTimeHint : "Starting",
        icon: <SpinnerRegular className={Icon16} />,
        cardClass: styles.CardInfo,
        statusClass: styles.StatusInfo
      }
    }
    case "Running":
      return {
        text: "Running",
        icon: <CheckmarkRegular className={Icon16} />,
        cardClass: styles.CardSuccess,
        statusClass: styles.StatusSuccess
      }
    case "Failed":
      return {
        text: "Failed",
        icon: <StopRegular className={Icon16} />,
        cardClass: styles.CardError,
        statusClass: styles.StatusError
      }
    default:
      return {
        text: "Stopped",
        icon: <ClockRegular className={Icon16} />,
        cardClass: styles.CardDisabled,
        statusClass: styles.StatusDisabled
      }
  }
}

const SshConnectionDialogContent = observer((
  {
    previewModel,
    isLoadingSshCommand
  }: {
    previewModel: TaskPreviewModel
    isLoadingSshCommand: boolean
  }
) => {
  const [isAddingKey, setIsAddingKey] = useState(false)
  const connectCommand = previewModel.connectCommand.get()
  const keyCount = previewModel.sshKeyCount.get()

  // Show loader while data is being fetched or while adding a key
  if (isLoadingSshCommand || isAddingKey) {
    return <Spinner text={ellipsize("Loading")} />
  }

  if (keyCount === 0) {
    return (
      <SshKeysOnboarding onComplete={async () => {
        setIsAddingKey(true)
        const result = await previewModel.getSshConnectCommand()
        runInAction(() => {
          previewModel.connectCommand.set(result.command)
          previewModel.sshKeyCount.set(result.keys.length)
        })
        setIsAddingKey(false)
      }} showHeading={false} />
    )
  }

  return (
    <Vertical alignItems="flex-start" gap="8px">
      <CopyableCodeSnippet text={connectCommand ?? ""} language="sh" backgroundColor="var(--text-color-5)" />
    </Vertical>
  )
})

const PreviewEnvStatusCard = observer(({ model, previewModel, isReadonly }: {
  model: TaskRuntimeConfigFileModel
  previewModel: TaskPreviewModel
  isReadonly: boolean
}) => {
  const devMode = useDevMode()
  const previewState = previewModel.previewProcessesState.get()
  const [sshDialogOpen, setSshDialogOpen] = useState(false)
  const [isLoadingSshCommand, setIsLoadingSshCommand] = useState(false)
  const [statusProps, setStatusProps] = useState(getPreviewStatusProps(previewState, devMode))
  const squircleRef = useRef<HTMLDivElement | null>(null)
  const currentSquircle = squircleRef.current
  const lastProgress = useRef<number | null | undefined>(null)

  useEffect(() => {
    if (previewState?.className !== "Starting") {
      lastProgress.current = null
    }
    const transitionClearTimeout = setTimeout(() => {
      if (previewState?.className !== "Starting") {
        currentSquircle?.style.setProperty("--card-progress-translate", "-50%")
        currentSquircle?.style.setProperty("--card-progress-opacity", "0")
      }
    }, 2000)

    const progressInterval = setInterval(() => {
      if (!devMode) {
        return
      }
      let progress = getStartingProgress(previewState)?.progress
      if (previewState?.className === "Starting" && progress === undefined && lastProgress.current !== null) {
        progress = lastProgress.current
      }
      if (progress !== lastProgress.current) {
        if (progress) {
          currentSquircle?.style.setProperty("--card-progress-translate", `${progress * 100 - 50}%`)
          currentSquircle?.style.setProperty("--card-progress-opacity", "1")
        } else {
          currentSquircle?.style.setProperty("--card-progress-translate", "50%")
          currentSquircle?.style.setProperty("--card-progress-opacity", "0")
        }
      }
      lastProgress.current = progress
    }, 100)

    const statusInterval = setInterval(() => {
      setStatusProps(getPreviewStatusProps(previewState, devMode))
    }, 1000)

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval)
      }
      clearInterval(statusInterval)
      clearTimeout(transitionClearTimeout)
    }
  }, [previewState, currentSquircle, devMode])

  useEffect(() => {
    if (sshDialogOpen) {
      previewModel.onConnectButtonPress(true)
    } else {
      previewModel.onConnectButtonPress(false)
    }
  }, [sshDialogOpen, previewModel])

  const handleConnectClick = () => {
    setIsLoadingSshCommand(true)

    const loadCommand = from(previewModel.getSshConnectCommand()).pipe(
      tap(result => {
        runInAction(() => {
          previewModel.connectCommand.set(result.command)
          previewModel.sshKeyCount.set(result.keys.length)
        })
      })
    )

    // - If data loads within 250ms, open the dialog immediately with data ready
    // - If it takes longer, open the dialog after 250ms and show a spinner until at least 1 second
    //   has passed from dialog opening, preventing a jarring flash
    race(
      loadCommand.pipe(tap(() => {
        setIsLoadingSshCommand(false)
        setSshDialogOpen(true)
      })),
      timer(250).pipe(
        tap(() => setSshDialogOpen(true)),
        switchMap(() => combineLatest([loadCommand, timer(1000)]).pipe(
          tap(() => setIsLoadingSshCommand(false))
        ))
      )
    ).subscribe()
  }

  return (
    <Squircle className={cx(Card, statusProps.cardClass, styles.CardProgress) as LinariaClassName} ref={squircleRef}>
      <FullWidthRow alignItems="flex-start" gap="8px">
        <FullWidthColumn alignItems="flex-start" gap="4px">
          <FullWidthRow alignItems="center" gap="8px">
            <CardTitle>Preview Environment</CardTitle>
            <Dialog
              title="Remote Environment Info"
              trigger={
                <Button kind="icon" size="small" aria-label="Environment info">
                  <InfoRegular className={Icon16} />
                </Button>
              }
            >
              <PreviewRuntimeInfo text={model.runtimeEnvDescription.get() ?? ellipsize("Loading environment info")} />
            </Dialog>

            <Spring />
            <Status props={statusProps} />
          </FullWidthRow>
          {previewState?.className === "Starting" && previewState.status !== null && (
            <CardDescription>
              <MarkdownWrapper text={previewState.status} />
            </CardDescription>
          )}
          {previewState?.className === "Failed" && previewState.message !== null && (
            <CardDescription>
              <MarkdownWrapper text={previewState.message} />
            </CardDescription>
          )}
        </FullWidthColumn>
      </FullWidthRow>
      <FullWidthRow>
        {devMode && previewModel && previewModel.previewProcessesState.get() && (
          <DownloadRuntimeLogsButton model={previewModel} className={styles.DownloadRuntimeLogsButton} />
        )}
        {previewModel && previewModel.canSshToEnvironment.get() && (
          <Dialog
            open={sshDialogOpen}
            onOpenChange={setSshDialogOpen}
            trigger={
              <CardButton kind="alternative" onClick={handleConnectClick}>
                <TerminalRegular className={Icon16} />
                Connect via SSH
              </CardButton>
            }
            title="Connect via SSH"
            maxWidth="640px"
          >
            <SshConnectionDialogContent previewModel={previewModel} isLoadingSshCommand={isLoadingSshCommand} />
          </Dialog>
        )}
        <Spring />
        <Tooltip content={<>This process is resource-intensive<br />and can take a while</>} align="end">
          {!model.isAnyScriptRunning.get() && (
            <CardButton
              kind="alternative"
              onClick={model.handleRecreatePreviewRuntime}
              disabled={isReadonly}
            >
              Recreate
            </CardButton>
          )}
        </Tooltip>
      </FullWidthRow>
    </Squircle>
  )
})

const Status = ({ props }: { props: StatusProps }) => {
  return (
    <StatusBadge className={cx(props.statusClass)}>
      {props.icon}
      {props.text}
    </StatusBadge>
  )
}

const Container = styled(Vertical)`
  align-items: flex-start;
  gap: 24px;

  width: 100%;
`

const HeaderContainer = styled(Vertical)`
  gap: 24px;

  width: 100%;
  margin-top: 8px;
`

const HeaderLine = styled(Horizontal)`
  align-items: center;
  gap: 16px;

  width: 100%;
`

const Card = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  width: 100%;
  padding: 12px 16px 16px;

  filter: none;

  --squircle-border-radius: 24px;
  --squircle-fill: unset;
`

const StatusBadge = styled(Horizontal)`
  align-items: center;
  align-self: flex-start;
  gap: 4px;

  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: 0.5px;

  text-transform: uppercase;
`

const RunButton = styled(Button)`
  width: 36px;
  height: 36px;

  background-color: #5B5CE2;
  border-radius: var(--border-radius-round);
`

const styles = {
  CardSuccess: css`
    background: linear-gradient(180deg, rgb(16 185 129 / 12%) 0%, rgb(16 185 129 / 8%) 100%);
    mask-image: var(--paint-squircle);
  `,

  CardError: css`
    background: linear-gradient(180deg, rgb(244 63 94 / 12%) 0%, rgb(244 63 94 / 8%) 100%);
    mask-image: var(--paint-squircle);
  `,

  CardInfo: css`
    background: linear-gradient(180deg, rgb(59 130 246 / 12%) 0%, rgb(59 130 246 / 8%) 100%);
    mask-image: var(--paint-squircle);
  `,

  CardProgress: css`
    --card-progress-translate: -50%;
    --card-progress-opacity: 0;
    position: relative;

    &::before {
      position: absolute;
      inset: 0;
      content: '';
      pointer-events: none;
      transition: transform 0.8s ease-out, opacity 1.0s ease-in;
      opacity: var(--card-progress-opacity);
      background: linear-gradient(to right, transparent 0%, rgb(59 130 246 / 16%) 50%, transparent 50%, transparent 100%);
      transform: translateX(var(--card-progress-translate));
    }
  `,

  CardDisabled: css`
    background: linear-gradient(180deg, rgb(148 163 184 / 12%) 0%, rgb(148 163 184 / 8%) 100%);
    mask-image: var(--paint-squircle);
  `,

  StatusSuccess: css`
    color: #00A255;
  `,

  StatusError: css`
    color: #E91647;
  `,

  StatusInfo: css`
    color: var(--main-color);
  `,

  StatusDisabled: css`
    color: var(--secondary-text-color);
  `,

  HoverOnly: css`
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease-in-out;

    .${Card}:hover & {
      opacity: 1;
      pointer-events: auto;
    }
  `,

  DownloadRuntimeLogsButton: css`
    background: transparent;
    border-color: var(--text-color-10);
  `,
}

const CardTitle = styled(Heading3)`
  ${textStyles.breakWord};

  color: var(--text-color);
  font-weight: 500;
`

const CardDescription = styled.div`
  width: min(80%, 600px);

  ${textStyles.smallFontSize};
  ${textStyles.breakWord};

  color: var(--secondary-text-color);
`

const FullWidthRow = styled(Horizontal)`
  gap: 8px;

  width: 100%;
`

const FullWidthColumn = styled(Vertical)`
  align-items: flex-start;
  gap: 8px;

  width: 100%;
`

const InlineAction = styled(Horizontal)`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ButtonWithIcon = styled(Button)`
  gap: 4px;
`

const CardButton = styled(ButtonWithIcon)`
  background: transparent;
  border-color: var(--text-color-10);
`

const PreviewRuntimeInfo = styled(MarkdownWrapper)`
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;

  .Markdown-Heading4 {
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: -0.01px;
  }
`
